import { Form, FormProps } from "@/components/form";
import { localNow } from "@/shared/local-now";
import { ConfirmDialog, useOpenModal } from "@/components/modal";
import { Grid2 as Grid, Stack, Typography } from "@mui/material";
import React from "react";
import { z } from "zod/v4";
import VentaGeneralInfoFields from "./venta-general-info-fields";
import VentaArticuloSearch from "./venta-articulo-search";
import VentaArticuloList from "./venta-articulo-list";
import { notifyError } from "@/shared/notification";
import useHasPermisos from "@/features/auth/hooks/useHasPermisos";
import { Permiso } from "@/shared/types/permiso";

export const ventaFormSchema = z.object({
    vendedorId: z.string().nonempty("Este campo es requerido"),
    clienteId: z.string().nonempty("Este campo es requerido"),
    fecha: z.iso.datetime({ local: true, message: "Este campo es requerido" }).default(localNow()),
    credito: z.boolean(),
    presupuesto: z.boolean(),
    items: z.array(z.object({
        articuloId: z.string().nonempty("Este campo es requerido"),
        articuloNombre: z.string().optional(),
        articuloCodigo: z.string().optional(),
        cantidad: z.number().min(1, "La cantidad debe ser mayor que 0"),
        precioUnitario: z.number().min(1, "El costo debe ser mayor que 0"),
        precioIndex: z.number()
    })).min(1, "Debe haber por lo menos un ítem"),
    token: z.string().optional(),
    auto: z.boolean().default(true)
})

export function VentaForm({
    form,
    onAfterReset,
    draftRestoreSlot,
    ...props
}: FormProps<z.input<typeof ventaFormSchema>> & { onAfterReset?: () => void; draftRestoreSlot?: React.ReactNode }) {
    const openModal = useOpenModal();
    const hasAllowNegativeStock = useHasPermisos(Permiso.AllowNegativeStock);

    const handleReset = () => {
        const vendedorId = form.getFieldValue("vendedorId");
        form.reset();
        form.setFieldValue("vendedorId", vendedorId);
        onAfterReset?.();
    }

    return <Form form={form} gap={3} {...props}>
        <VentaGeneralInfoFields />
        <Grid container columns={{ xs: 1, md: 3 }} spacing={5} flex={1}>
            <Grid component={Stack} size={1}>
                <VentaArticuloSearch onArticuloSelect={(v) => {
                    const presupuesto = form.getFieldValue("presupuesto");

                    if (!presupuesto && !hasAllowNegativeStock && v.articulo.cantidad < v.cantidad) {
                        notifyError(`No hay suficiente stock del artículo ${v.articulo.nombre}. Stock disponible: ${v.articulo.cantidad}`);
                        return;
                    }

                    const items = form.getFieldValue("items");
                    const index = items.findIndex(i => i.articuloId === v.articulo.id)
                    if (index != -1) {
                        const previousValue = items[index];
                        const newValue = { ...previousValue, cantidad: previousValue.cantidad + v.cantidad }
                        form.replaceFieldValue("items", index, newValue);
                    } else {
                        form.pushFieldValue("items", {
                            articuloId: v.articulo.id,
                            articuloNombre: v.articulo.nombre,
                            articuloCodigo: v.articulo.codigo,
                            cantidad: v.cantidad,
                            precioIndex: v.precioIndex,
                            precioUnitario: v.precio
                        })
                    }
                }} />
            </Grid>
            <Grid component={Stack} size={2} gap={3}>
                <form.Field name="items" mode="array">
                    {field => <>
                        <VentaArticuloList
                            items={field.state.value}
                            remove={field.removeValue}
                        />
                        {field.state.meta.errors.length > 0 && (
                            <Typography color="error" variant="body2">
                                {field.state.meta.errors[0]?.message ?? field.state.meta.errors[0]}
                            </Typography>
                        )}
                    </>}
                </form.Field>
                <Stack direction={"row"} alignItems={"center"} gap={3}>
                    {draftRestoreSlot}
                    <Stack direction={"row"} justifyContent={"end"} gap={3} flex={1}>
                        <Form.Button onClick={() => openModal("confirmReset")}>Cancelar</Form.Button>
                        <Form.SubmitButton>Confirmar</Form.SubmitButton>
                    </Stack>
                </Stack>
            </Grid>
        </Grid>

        <ConfirmDialog code="confirmReset">
            <ConfirmDialog.Content
                title="Cancelar Venta"
                description="¿Estas seguro que quieres cancelar la venta actual?"
                onConfirm={handleReset}
            />
        </ConfirmDialog>
    </Form>
}